"use client";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BASE_URL } from "../config/config";

function EmailVerificationPending() {
  const navigate = useNavigate();
  const [isResending, setIsResending] = React.useState(false);
  const [resendMessage, setResendMessage] = React.useState("");
  const [userEmail, setUserEmail] = React.useState("");

  // Get email from localStorage or URL params if available
  React.useEffect(() => {
    const email = localStorage.getItem("pendingVerificationEmail") ||
                  new URLSearchParams(window.location.search).get("email") ||
                  "";
    setUserEmail(email);
  }, []);

  const handleLogin = () => navigate("/login");
  const handleReturnToStore = () => navigate("/");

  const handleResendEmail = async () => {
    if (!userEmail) {
      setResendMessage("Email address not found. Please try signing up again.");
      return;
    }

    setIsResending(true);
    setResendMessage("");

    try {
      const response = await fetch(`${BASE_URL}/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendMessage("Verification email sent successfully! Please check your inbox.");
      } else {
        setResendMessage(data.message || "Failed to resend email. Please try again.");
      }
    } catch (error) {
      setResendMessage("An error occurred. Please try again later.");
      console.error("Resend email error:", error);
    } finally {
      setIsResending(false);
    }
  };

  const underlineVariants = {
    initial: { scaleX: 0 },
    hover: { scaleX: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { scaleX: 0, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  const AnimatedLink = ({ children, onClick, ariaLabel }) => (
    <motion.button
      onClick={onClick}
      className="relative text-base cursor-pointer text-yellow-500 bg-transparent border-none p-0 hover:opacity-80 outline-none"
      aria-label={ariaLabel}
      whileHover="hover"
      initial="initial"
      animate="initial"
    >
      <span className="relative z-10">{children}</span>
      <motion.span
        className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-center"
        variants={underlineVariants}
      />
    </motion.button>
  );

  const EmailIcon = () => (
    <motion.svg
      className="h-10 w-10 text-yellow-500"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
    >
      <motion.path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
      />
    </motion.svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <motion.div
        className="flex flex-col items-center gap-10 p-10 bg-white rounded-3xl shadow-lg max-w-md w-full"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Email Icon */}
        <motion.div
          className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-yellow-100 relative"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
        >
          <EmailIcon />
          {/* Animated pulse effect */}
          <motion.div
            className="absolute inset-0 rounded-full bg-yellow-200"
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 1.2, opacity: 0 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeOut"
            }}
          />
        </motion.div>

        {/* Header */}
        <motion.header
          className="text-center"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h1 className="text-4xl font-bold text-yellow-500 mb-2">Check Your Email</h1>
          <p className="text-gray-600 text-base leading-relaxed">
            We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
          </p>
        </motion.header>

        {/* Email Display */}
        {userEmail && (
          <motion.div
            className="text-center bg-yellow-50 p-4 rounded-2xl border border-yellow-200 w-full"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p className="text-gray-700 text-sm mb-2">Verification email sent to:</p>
            <p className="text-yellow-600 font-medium text-sm break-all">{userEmail}</p>
          </motion.div>
        )}

        {/* Instructions */}
        <motion.div
          className="text-center bg-gray-50 p-6 rounded-2xl border border-gray-200 w-full"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <h3 className="text-gray-800 font-semibold mb-3">What to do next:</h3>
          <ul className="text-gray-600 text-sm space-y-2 text-left">
            <li className="flex items-start gap-2">
              <span className="text-yellow-500 font-bold">1.</span>
              <span>Check your email inbox (and spam folder)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-500 font-bold">2.</span>
              <span>Click the verification link in the email</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-500 font-bold">3.</span>
              <span>Return here to sign in to your account</span>
            </li>
          </ul>
        </motion.div>

        {/* Resend Message */}
        {resendMessage && (
          <motion.div
            className={`text-center p-3 rounded-lg w-full ${
              resendMessage.includes("successfully")
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-sm">{resendMessage}</p>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          className="flex flex-col gap-4 w-full"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
        
        {/* Email input */}
        <div className="w-full">
            <label className="block text-gray-700 text-sm font-medium mb-1" htmlFor="resend-email">
            Email Address
            </label>
            <input
            id="resend-email"
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            />
        </div>

          <button
            onClick={handleResendEmail}
            disabled={isResending}
            className="w-full px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            aria-label="Resend verification email"
          >
            {isResending ? "Sending..." : "Resend Verification Email"}
          </button>

          <button
            onClick={handleLogin}
            className="w-full px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg shadow-sm transition-all"
            aria-label="Go to login page"
          >
            Already Verified? Sign In
          </button>

          <div className="flex justify-center pt-2">
            <AnimatedLink onClick={handleReturnToStore} ariaLabel="Return to store">
              Return to Store
            </AnimatedLink>
          </div>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          className="text-center text-xs text-gray-500 border-t border-gray-200 pt-6 w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <p className="mb-2">
            Didn't receive the email? Check your spam folder or try resending.
          </p>
          <p>
            If you continue to have issues, please contact our support team.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default EmailVerificationPending;